import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Textarea from '../../components/ui/Textarea';
import {
  createMcpServer,
  getMcpServer,
  testMcpServerConnection,
  updateMcpServer,
} from '../../api/mcpServers';
import { getApiErrorMessage } from '../../utils/errors';
import {
  createIntegrationApp,
  deleteIntegrationApp,
  listIntegrationApps,
  updateIntegrationApp,
} from '../../api/integrationApps';
import {
  buildMcpServerPayload,
  EMPTY_MCP_SERVER_FORM,
  mapMcpServerFromApi,
  MCP_AUTH_TYPE_OPTIONS,
  MCP_TRANSPORT_OPTIONS,
  OAUTH_TOKEN_AUTH_METHOD_OPTIONS,
} from './constants';

function McpServerForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(EMPTY_MCP_SERVER_FORM);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [contactApp, setContactApp] = useState({
    id: null,
    enabled: false,
    name: '',
    description: '',
    iconUrl: '',
    clientId: '',
    clientSecret: '',
    authorizationUrl: '',
    tokenUrl: '',
    revokeUrl: '',
    profileUrl: '',
    scopes: '',
    tokenAuthMethod: '',
    secretConfigured: false,
  });

  const loadEntry = useCallback(async () => {
    if (!id) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      const entry = await getMcpServer(id);
      setForm(mapMcpServerFromApi(entry));
      const apps = await listIntegrationApps();
      const app = (apps.data || []).find((item) => item.mcp_server_id === id);
      setContactApp({
        id: null,
        enabled: false,
        name: '',
        description: '',
        iconUrl: '',
        clientId: '',
        clientSecret: '',
        authorizationUrl: '',
        tokenUrl: '',
        revokeUrl: '',
        profileUrl: '',
        scopes: '',
        tokenAuthMethod: '',
        secretConfigured: false,
      });
      if (app) {
        setContactApp({
          id: app.id,
          enabled: true,
          name: app.name || '',
          description: app.description || '',
          iconUrl: app.icon_url || '',
          clientId: app.oauth_client_id || '',
          clientSecret: '',
          authorizationUrl: app.oauth_configurations?.authorization_url || '',
          tokenUrl: app.oauth_configurations?.token_url || '',
          revokeUrl: app.oauth_configurations?.revoke_url || '',
          profileUrl: app.oauth_configurations?.profile_url || '',
          scopes: (app.oauth_configurations?.scopes || []).join(' '),
          tokenAuthMethod: app.oauth_configurations?.token_endpoint_auth_method || '',
          secretConfigured: app.oauth_client_secret_configured,
        });
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível carregar o servidor MCP.'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadEntry();
  }, [loadEntry]);

  const handleChange = (field) => (event) => {
    const value =
      event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [field]: value }));
    setSuccess('');
    setTestResult(null);
  };

  const isPublicOauthClient = contactApp.tokenAuthMethod === 'none';

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const payload = buildMcpServerPayload(form, { isEdit });
      let savedServer;
      if (isEdit) {
        savedServer = await updateMcpServer(id, payload);
      } else {
        savedServer = await createMcpServer(payload);
      }
      if (contactApp.enabled) {
        const appPayload = {
          mcp_server_id: savedServer.id,
          slug: savedServer.slug,
          name: contactApp.name.trim() || savedServer.label,
          description: contactApp.description.trim() || null,
          icon_url: contactApp.iconUrl.trim() || null,
          oauth_client_id: contactApp.clientId.trim(),
          oauth_client_secret: isPublicOauthClient
            ? null
            : contactApp.clientSecret.trim() || null,
          oauth_configurations: {
            authorization_url: contactApp.authorizationUrl.trim(),
            token_url: contactApp.tokenUrl.trim(),
            revoke_url: contactApp.revokeUrl.trim() || undefined,
            profile_url: contactApp.profileUrl.trim() || undefined,
            scopes: contactApp.scopes.split(/\s+/).filter(Boolean),
            token_endpoint_auth_method: contactApp.tokenAuthMethod || undefined,
          },
          is_active: true,
        };
        if (contactApp.id) {
          await updateIntegrationApp(contactApp.id, appPayload);
        } else {
          await createIntegrationApp(appPayload);
        }
      } else if (contactApp.id) {
        await deleteIntegrationApp(contactApp.id);
      }
      if (isEdit) {
        setSuccess('Servidor MCP atualizado.');
        await loadEntry();
      } else {
        navigate(`/mcp-servers/${savedServer.id}/edit`, {
          replace: true,
          state: { created: true },
        });
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível salvar o servidor MCP.'));
    } finally {
      setSaving(false);
    }
  };

  const handleContactAppChange = (field) => (event) => {
    const value =
      event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setContactApp((current) => ({ ...current, [field]: value }));
  };

  const handleTestConnection = async () => {
    if (!isEdit) {
      setError('Salve o servidor antes de testar a conexão.');
      return;
    }

    setTesting(true);
    setError('');
    setTestResult(null);

    try {
      const result = await testMcpServerConnection(id);
      setTestResult(result);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Falha ao testar conexão com o servidor MCP.'));
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="surface-card px-4 py-12 text-center max-w-xl mx-auto">
        <p className="text-content-muted text-sm">Carregando servidor...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-xl mx-auto">
      <div>
        <Link to="/mcp-servers" className="text-sm text-content-muted hover:text-forge-600">
          ← Voltar para servidores MCP
        </Link>
        <h1 className="text-2xl font-bold text-content-primary mt-3">
          {isEdit ? 'Editar servidor MCP' : 'Novo servidor MCP'}
        </h1>
        <p className="text-content-muted text-sm mt-1">
          Configure URL, transporte e autenticação do servidor MCP.
        </p>
      </div>

      <div className="surface-card p-8 shadow-card-md">
        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 mb-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
        {success && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 mb-4">
            <p className="text-sm text-emerald-700">{success}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isEdit && (
            <Input
              id="slug"
              label="Slug"
              name="slug"
              value={form.slug}
              onChange={handleChange('slug')}
              placeholder="meu-servidor"
              required
              hint="Identificador único na conta (letras minúsculas, números e hífens)."
            />
          )}

          <Input
            id="label"
            label="Nome"
            name="label"
            value={form.label}
            onChange={handleChange('label')}
            placeholder="Nome do servidor"
            required
          />

          <Input
            id="url"
            label="URL"
            name="url"
            value={form.url}
            onChange={handleChange('url')}
            placeholder="https://servidor.exemplo.com/mcp"
            required
          />

          <Select
            id="transport"
            label="Transporte"
            value={form.transport}
            onChange={handleChange('transport')}
            options={MCP_TRANSPORT_OPTIONS}
          />

          <Select
            id="auth_type"
            label="Autenticação"
            value={form.auth_type}
            onChange={handleChange('auth_type')}
            options={MCP_AUTH_TYPE_OPTIONS}
          />

          {form.auth_type === 'bearer' && (
            <Input
              id="token"
              label="Bearer token"
              name="token"
              type="password"
              value={form.token}
              onChange={handleChange('token')}
              placeholder={form.tokenConfigured ? 'Deixe em branco para manter o token atual' : 'Token de acesso'}
              required={!isEdit && !form.tokenConfigured}
            />
          )}

          <label className="flex items-center gap-2 text-sm text-content-primary">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={handleChange('is_active')}
              className="rounded border-slate-300"
            />
            Servidor ativo
          </label>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-4">
            <label className="flex items-start gap-2 text-sm text-content-primary">
              <input
                type="checkbox"
                checked={contactApp.enabled}
                onChange={handleContactAppChange('enabled')}
                className="mt-0.5 rounded border-slate-300"
              />
              <span>
                Disponibilizar no espaço do contato
                <span className="block text-xs text-content-muted">
                  Cada pessoa autoriza a própria conta por OAuth.
                </span>
              </span>
            </label>
            {contactApp.enabled && (
              <div className="space-y-4 border-t border-slate-200 pt-4">
                <Input
                  id="contact_app_name"
                  label="Nome para o contato"
                  value={contactApp.name}
                  onChange={handleContactAppChange('name')}
                  placeholder={form.label || 'Strava'}
                  required
                />
                <Textarea
                  id="contact_app_description"
                  label="Descrição"
                  value={contactApp.description}
                  onChange={handleContactAppChange('description')}
                  rows={2}
                  placeholder="Explique quais dados serão usados e para quê."
                />
                <Input
                  id="contact_app_icon"
                  label="URL do ícone (opcional)"
                  value={contactApp.iconUrl}
                  onChange={handleContactAppChange('iconUrl')}
                />
                <Input
                  id="oauth_client_id"
                  label="OAuth Client ID"
                  value={contactApp.clientId}
                  onChange={handleContactAppChange('clientId')}
                  required
                />
                <Select
                  id="oauth_token_endpoint_auth_method"
                  label="Autenticação no token endpoint"
                  value={contactApp.tokenAuthMethod}
                  onChange={handleContactAppChange('tokenAuthMethod')}
                  options={OAUTH_TOKEN_AUTH_METHOD_OPTIONS}
                  hint="Cliente público envia só o client_id, sem segredo."
                />
                {!isPublicOauthClient && (
                  <Input
                    id="oauth_client_secret"
                    label="OAuth Client Secret (opcional)"
                    type="password"
                    value={contactApp.clientSecret}
                    onChange={handleContactAppChange('clientSecret')}
                    placeholder={
                      contactApp.secretConfigured
                        ? 'Deixe vazio para manter o segredo atual'
                        : 'Deixe vazio se o provedor não emitiu segredo'
                    }
                  />
                )}
                <Input
                  id="oauth_authorization_url"
                  label="URL de autorização"
                  value={contactApp.authorizationUrl}
                  onChange={handleContactAppChange('authorizationUrl')}
                  placeholder="https://provider.com/oauth/authorize"
                  required
                />
                <Input
                  id="oauth_token_url"
                  label="URL de token"
                  value={contactApp.tokenUrl}
                  onChange={handleContactAppChange('tokenUrl')}
                  placeholder="https://provider.com/oauth/token"
                  required
                />
                <Input
                  id="oauth_scopes"
                  label="Escopos"
                  value={contactApp.scopes}
                  onChange={handleContactAppChange('scopes')}
                  placeholder="read activity:read"
                  hint="Separe os escopos por espaço."
                />
                <Input
                  id="oauth_profile_url"
                  label="URL de perfil (opcional)"
                  value={contactApp.profileUrl}
                  onChange={handleContactAppChange('profileUrl')}
                />
                <Input
                  id="oauth_revoke_url"
                  label="URL de revogação (opcional)"
                  value={contactApp.revokeUrl}
                  onChange={handleContactAppChange('revokeUrl')}
                />
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button type="submit" disabled={saving} className="btn-primary text-sm px-4 py-2.5">
              {saving ? 'Salvando...' : isEdit ? 'Salvar alterações' : 'Cadastrar servidor'}
            </button>
            {isEdit && (
              <button
                type="button"
                onClick={handleTestConnection}
                disabled={testing}
                className="btn-secondary text-sm px-4 py-2.5"
              >
                {testing ? 'Testando...' : 'Testar conexão'}
              </button>
            )}
          </div>
        </form>

        {testResult && (
          <div className="mt-6 rounded-xl border border-forge-200 bg-forge-50 px-4 py-3">
            <p className="text-sm text-forge-800 font-medium">
              {testResult.source === 'edge_catalog' ? 'Catálogo Edge disponível' : 'Conexão OK'} ·{' '}
              {testResult.tool_count} ferramenta{testResult.tool_count !== 1 ? 's' : ''}
            </p>
            {testResult.tools?.length > 0 && (
              <ul className="mt-2 space-y-1">
                {testResult.tools.map((tool) => (
                  <li key={tool.name} className="text-xs text-forge-700">
                    <span className="font-mono">{tool.name}</span>
                    {tool.description ? ` — ${tool.description}` : ''}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default McpServerForm;
